/**
 * ObligationItem — renders one obligation row in the
 * SkillCommissioningPage modal's 8-Obligation Summary.
 *
 * WQ-263 Scope B.2 — replaces the WQ-245 hardcoded ✅/❌/—  list with
 * per-obligation Pass/Fail/Hold/N/A visual + click-to-expand subitem
 * detail. Pass-with-notes intentionally non-screaming (inline amber
 * badge, no row background); Hold/Fail get row-background treatment to
 * break visual scan. Expanded Pass-with-notes content carries the same
 * amber accent on border-left for collapsed→expanded visual lineage
 * per Owen 2026-05-05 refinement.
 */

import React, { useState } from 'react';

export type ObligationResult = 'pass' | 'fail' | 'hold' | 'n_a';
export type SubitemResultValue =
  | 'pass'
  | 'fail'
  | 'hold'
  | 'n_a'
  | 'pending_first_invocation';

export interface SubitemResult {
  subitem_number: number;
  description: string;
  result: SubitemResultValue;
  evidence_pointer?: string | null;
  notes?: string | null;
}

export interface ObligationEvaluation {
  obligation_number: number;
  obligation_name: string;
  result: ObligationResult;
  subitem_results: SubitemResult[];
  evaluator_notes?: string;
}

interface ObligationItemProps {
  obligation: ObligationEvaluation;
}

const PASS_WITH_NOTES_AMBER = '#f59e0b';
const PASS_WITH_NOTES_BG = 'rgba(245,158,11,0.15)';
const PASS_WITH_NOTES_TEXT = '#92400e';

function obligationHasNotes(o: ObligationEvaluation): boolean {
  return o.subitem_results.some((s) => Boolean(s.notes));
}

const ObligationItem: React.FC<ObligationItemProps> = ({ obligation }) => {
  const [expanded, setExpanded] = useState(false);
  const passWithNotes =
    obligation.result === 'pass' && obligationHasNotes(obligation);

  // Visual hierarchy per WQ-263 Engagement A B.2 decision:
  //   Pass         → green check, no badge, no row background
  //   Pass-w-notes → green check + amber inline badge, no row background
  //   N/A          → em-dash, gray, justification inline
  //   Hold         → yellow circle + amber/yellow row background
  //   Fail         → red X + red row background
  let icon = '';
  let label = '';
  let rowBg: string | undefined;
  let labelColor: string | undefined;
  if (obligation.result === 'pass') {
    icon = '✅';
    label = 'Pass';
  } else if (obligation.result === 'fail') {
    icon = '❌';
    label = 'Fail';
    rowBg = 'rgba(220,38,38,0.10)';
  } else if (obligation.result === 'hold') {
    icon = '🟡';
    label = 'Hold';
    rowBg = 'rgba(245,158,11,0.08)';
  } else if (obligation.result === 'n_a') {
    icon = '—';
    label = 'N/A';
    labelColor = 'var(--text-muted)';
  }

  const handleToggle = () => {
    if (obligation.subitem_results.length > 0) {
      setExpanded((v) => !v);
    }
  };

  return (
    <li
      data-testid={`obligation-row-${obligation.obligation_number}`}
      data-result={obligation.result}
      data-pass-with-notes={passWithNotes ? 'true' : 'false'}
      style={{
        marginBottom: 'var(--space-1)',
        padding: 'var(--space-2)',
        background: rowBg,
        borderRadius: 'var(--radius-sm)',
        cursor: obligation.subitem_results.length > 0 ? 'pointer' : 'default',
        listStyle: 'none',
      }}
      onClick={handleToggle}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span style={{ fontSize: 'var(--text-base)' }}>{icon}</span>
        <strong style={{ color: labelColor }}>
          Obligation {obligation.obligation_number}
        </strong>
        <span style={{ color: labelColor }}>({obligation.obligation_name})</span>
        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>
          — {label}
        </span>
        {passWithNotes && (
          <span
            data-testid={`obligation-${obligation.obligation_number}-pass-with-notes-badge`}
            style={{
              fontSize: '12px',
              background: PASS_WITH_NOTES_BG,
              color: PASS_WITH_NOTES_TEXT,
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            with notes
          </span>
        )}
        {obligation.subitem_results.length > 0 && (
          <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>
            {expanded ? '▾' : '▸'}
          </span>
        )}
      </div>
      {expanded && obligation.subitem_results.length > 0 && (
        <div
          data-testid={`obligation-${obligation.obligation_number}-expanded`}
          style={{
            marginTop: 'var(--space-2)',
            paddingLeft: 'var(--space-3)',
            // WQ-263 amber lineage per Owen 2026-05-05 refinement: when
            // collapsed shows the amber pass-with-notes badge, the expanded
            // section preserves the visual lineage with amber border-left.
            borderLeft: passWithNotes
              ? `3px solid ${PASS_WITH_NOTES_AMBER}`
              : obligation.result === 'fail'
              ? '3px solid #dc2626'
              : obligation.result === 'hold'
              ? `3px solid ${PASS_WITH_NOTES_AMBER}`
              : '3px solid var(--border-secondary)',
            fontSize: 'var(--text-sm)',
          }}
        >
          {obligation.evaluator_notes && (
            <p style={{ color: 'var(--text-secondary)', margin: '0 0 var(--space-2) 0' }}>
              {obligation.evaluator_notes}
            </p>
          )}
          <ul style={{ paddingLeft: 'var(--space-4)', margin: 0 }}>
            {obligation.subitem_results.map((s) => (
              <li key={s.subitem_number} style={{ marginBottom: 'var(--space-1)' }}>
                <strong>Subitem {s.subitem_number}:</strong> {s.description} —{' '}
                <em style={{ color: 'var(--text-muted)' }}>{s.result}</em>
                {s.notes && (
                  <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>
                    {s.notes}
                  </div>
                )}
                {s.evidence_pointer && (
                  <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>
                    <code>{s.evidence_pointer}</code>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
};

export default ObligationItem;
